/* *
 * Client-side Pricing Data
 * Mirrors shared/constants/services/pricing.js for static website
 * @module PricingData */

"use strict";

// Discount Configuration
const DISCOUNT_CONFIG = {
  INTERNAL_COMBO_PERCENT: 5, // A+B Complete package
  EXTERNAL_COMBO_PERCENT: 10, // 2+ different services
  MAX_MANUAL_DISCOUNT: 50,
  MIN_PRICE: 10,
};

// Career Services
const CAREER_SERVICES = {
  resume: {
    id: "resume",
    name: "Resume Writing",
    hasWordRate: false,
    oneTimePrice: 299,
    deliveryDays: 5,
  },
  cover_letter: {
    id: "cover_letter",
    name: "Cover Letter Writing",
    hasWordRate: false,
    oneTimePrice: 199,
    deliveryDays: 3,
  },
  linkedin: {
    id: "linkedin",
    name: "LinkedIn Optimization",
    hasWordRate: false,
    oneTimePrice: 349,
    deliveryDays: 5,
  },
  lor: {
    id: "lor",
    name: "Letter of Recommendation",
    hasWordRate: false,
    oneTimePrice: 199,
    deliveryDays: 3,
  },
  sop: {
    id: "sop",
    name: "Statement of Purpose",
    hasWordRate: false,
    oneTimePrice: 299,
    deliveryDays: 5,
  },
  web_resume: {
    id: "web_resume",
    name: "Web Resume",
    hasWordRate: false,
    oneTimePrice: 599,
    deliveryDays: 10,
  },
  personal_website: {
    id: "personal_website",
    name: "Personal Website",
    hasWordRate: false,
    oneTimePrice: 999,
    deliveryDays: 14,
  },
  job_search: {
    id: "job_search",
    name: "Job Search Strategy",
    hasWordRate: false,
    hasPackages: true,
    packages: {
      a: { name: "Package A: Strategy", price: 249 },
      b: { name: "Package B: Execution", price: 499 },
      complete: {
        name: "Complete Package",
        price: 711,
        originalPrice: 748,
        discount: 5,
      },
    },
    deliveryDays: 7,
  },
  interview_prep: {
    id: "interview_prep",
    name: "Interview Preparation",
    hasWordRate: false,
    hasPackages: true,
    packages: {
      a: { name: "Package A: Preparation", price: 149 },
      b: { name: "Package B: Coaching", price: 449 },
      complete: {
        name: "Complete Package",
        price: 568,
        originalPrice: 598,
        discount: 5,
      },
    },
    deliveryDays: 7,
  },
  networking_email: {
    id: "networking_email",
    name: "Networking Email Templates",
    hasWordRate: false,
    hasPackages: true,
    packages: {
      a: { name: "Package A: Templates", price: 99 },
      b: { name: "Package B: Strategy", price: 399 },
      complete: {
        name: "Complete Package",
        price: 473,
        originalPrice: 498,
        discount: 5,
      },
    },
    deliveryDays: 5,
  },
  salary_negotiation: {
    id: "salary_negotiation",
    name: "Salary Negotiation",
    hasWordRate: false,
    hasPackages: true,
    packages: {
      a: { name: "Package A: Research", price: 149 },
      b: { name: "Package B: Coaching", price: 299 },
      complete: {
        name: "Complete Package",
        price: 426,
        originalPrice: 448,
        discount: 5,
      },
    },
    deliveryDays: 3,
  },
  reference_letters: {
    id: "reference_letters",
    name: "Reference Letter Management",
    hasWordRate: false,
    oneTimePrice: 149,
    deliveryDays: 3,
  },
  industry_transition: {
    id: "industry_transition",
    name: "Industry Transition",
    hasWordRate: false,
    oneTimePrice: 199,
    deliveryDays: 5,
  },
  personal_branding: {
    id: "personal_branding",
    name: "Personal Branding",
    hasWordRate: false,
    oneTimePrice: 349,
    deliveryDays: 7,
  },
};

// Academic Services
const ACADEMIC_SERVICES = {
  research_proposal: {
    id: "research_proposal",
    name: "Research Proposal",
    tiers: {
      proofreading: {
        undergraduate: { price: 49, wordRate: 0.03 },
        masters: { price: 69, wordRate: 0.04 },
        phd: { price: 89, wordRate: 0.05 },
      },
      draft_refining: {
        undergraduate: { price: 149, wordRate: 0.08 },
        masters: { price: 199, wordRate: 0.1 },
        phd: { price: 249, wordRate: 0.12 },
      },
      model_paper: {
        undergraduate: { price: 299, wordRate: 0.15 },
        masters: { price: 399, wordRate: 0.18 },
        phd: { price: 499, wordRate: 0.22 },
      },
    },
  },
  literature_review: {
    id: "literature_review",
    name: "Literature Review",
    tiers: {
      proofreading: {
        undergraduate: { price: 59, wordRate: 0.03 },
        masters: { price: 79, wordRate: 0.04 },
        phd: { price: 99, wordRate: 0.05 },
      },
      draft_refining: {
        undergraduate: { price: 179, wordRate: 0.09 },
        masters: { price: 229, wordRate: 0.11 },
        phd: { price: 279, wordRate: 0.13 },
      },
      model_paper: {
        undergraduate: { price: 349, wordRate: 0.16 },
        masters: { price: 449, wordRate: 0.19 },
        phd: { price: 549, wordRate: 0.23 },
      },
    },
  },
  scientific_paper: {
    id: "scientific_paper",
    name: "Scientific Paper",
    tiers: {
      proofreading: {
        undergraduate: { price: 69, wordRate: 0.04 },
        masters: { price: 89, wordRate: 0.05 },
        phd: { price: 119, wordRate: 0.06 },
      },
      draft_refining: {
        undergraduate: { price: 199, wordRate: 0.1 },
        masters: { price: 269, wordRate: 0.12 },
        phd: { price: 349, wordRate: 0.15 },
      },
      model_paper: {
        undergraduate: { price: 399, wordRate: 0.18 },
        masters: { price: 549, wordRate: 0.22 },
        phd: { price: 699, wordRate: 0.28 },
      },
    },
  },
  case_study: {
    id: "case_study",
    name: "Case Study",
    tiers: {
      proofreading: {
        undergraduate: { price: 49, wordRate: 0.03 },
        masters: { price: 69, wordRate: 0.04 },
        phd: { price: 89, wordRate: 0.05 },
      },
      draft_refining: {
        undergraduate: { price: 149, wordRate: 0.08 },
        masters: { price: 199, wordRate: 0.1 },
        phd: { price: 249, wordRate: 0.12 },
      },
      model_paper: {
        undergraduate: { price: 299, wordRate: 0.15 },
        masters: { price: 399, wordRate: 0.18 },
        phd: { price: 499, wordRate: 0.22 },
      },
    },
  },
  lab_report: {
    id: "lab_report",
    name: "Lab Report",
    tiers: {
      proofreading: {
        undergraduate: { price: 39, wordRate: 0.03 },
        masters: { price: 59, wordRate: 0.04 },
        phd: { price: 79, wordRate: 0.05 },
      },
      draft_refining: {
        undergraduate: { price: 119, wordRate: 0.07 },
        masters: { price: 169, wordRate: 0.09 },
        phd: { price: 219, wordRate: 0.11 },
      },
      model_paper: {
        undergraduate: { price: 249, wordRate: 0.14 },
        masters: { price: 349, wordRate: 0.17 },
        phd: { price: 449, wordRate: 0.21 },
      },
    },
  },
  annotated_bibliography: {
    id: "annotated_bibliography",
    name: "Annotated Bibliography",
    tiers: {
      proofreading: {
        undergraduate: { price: 39, wordRate: 0.03 },
        masters: { price: 49, wordRate: 0.04 },
        phd: { price: 69, wordRate: 0.05 },
      },
      draft_refining: {
        undergraduate: { price: 99, wordRate: 0.06 },
        masters: { price: 139, wordRate: 0.08 },
        phd: { price: 179, wordRate: 0.1 },
      },
      model_paper: {
        undergraduate: { price: 199, wordRate: 0.12 },
        masters: { price: 279, wordRate: 0.15 },
        phd: { price: 349, wordRate: 0.18 },
      },
    },
  },
  systematic_review: {
    id: "systematic_review",
    name: "Systematic Review",
    tiers: {
      proofreading: {
        undergraduate: { price: 79, wordRate: 0.04 },
        masters: { price: 99, wordRate: 0.05 },
        phd: { price: 129, wordRate: 0.06 },
      },
      draft_refining: {
        undergraduate: { price: 229, wordRate: 0.11 },
        masters: { price: 299, wordRate: 0.13 },
        phd: { price: 379, wordRate: 0.16 },
      },
      model_paper: {
        undergraduate: { price: 449, wordRate: 0.2 },
        masters: { price: 599, wordRate: 0.24 },
        phd: { price: 749, wordRate: 0.3 },
      },
    },
  },
  conference_paper: {
    id: "conference_paper",
    name: "Conference Paper/Poster",
    tiers: {
      proofreading: {
        undergraduate: { price: 59, wordRate: 0.04 },
        masters: { price: 79, wordRate: 0.05 },
        phd: { price: 99, wordRate: 0.06 },
      },
      draft_refining: {
        undergraduate: { price: 179, wordRate: 0.09 },
        masters: { price: 249, wordRate: 0.11 },
        phd: { price: 319, wordRate: 0.14 },
      },
      model_paper: {
        undergraduate: { price: 349, wordRate: 0.17 },
        masters: { price: 499, wordRate: 0.21 },
        phd: { price: 649, wordRate: 0.26 },
      },
    },
  },
  book_chapter: {
    id: "book_chapter",
    name: "Book Chapter",
    tiers: {
      proofreading: {
        undergraduate: { price: 89, wordRate: 0.05 },
        masters: { price: 119, wordRate: 0.06 },
        phd: { price: 149, wordRate: 0.07 },
      },
      draft_refining: {
        undergraduate: { price: 269, wordRate: 0.12 },
        masters: { price: 349, wordRate: 0.15 },
        phd: { price: 449, wordRate: 0.18 },
      },
      model_paper: {
        undergraduate: { price: 549, wordRate: 0.22 },
        masters: { price: 749, wordRate: 0.28 },
        phd: { price: 999, wordRate: 0.35 },
      },
    },
  },
  qualifying_exam: {
    id: "qualifying_exam",
    name: "Qualifying Exam Prep",
    tiers: {
      proofreading: {
        undergraduate: { price: 49, wordRate: 0.03 },
        masters: { price: 79, wordRate: 0.05 },
        phd: { price: 119, wordRate: 0.06 },
      },
      draft_refining: {
        undergraduate: { price: 149, wordRate: 0.08 },
        masters: { price: 229, wordRate: 0.11 },
        phd: { price: 319, wordRate: 0.14 },
      },
      model_paper: {
        undergraduate: { price: 299, wordRate: 0.15 },
        masters: { price: 449, wordRate: 0.2 },
        phd: { price: 649, wordRate: 0.28 },
      },
    },
  },
  term_paper: {
    id: "term_paper",
    name: "Term Paper",
    tiers: {
      proofreading: {
        undergraduate: { price: 39, wordRate: 0.03 },
        masters: { price: 59, wordRate: 0.04 },
        phd: { price: 79, wordRate: 0.05 },
      },
      draft_refining: {
        undergraduate: { price: 119, wordRate: 0.07 },
        masters: { price: 169, wordRate: 0.09 },
        phd: { price: 229, wordRate: 0.11 },
      },
      model_paper: {
        undergraduate: { price: 249, wordRate: 0.14 },
        masters: { price: 349, wordRate: 0.17 },
        phd: { price: 449, wordRate: 0.21 },
      },
    },
  },
};

// Helper Functions
function getCareerService(id) {
  return CAREER_SERVICES[id];
}

function getAcademicService(id) {
  return ACADEMIC_SERVICES[id];
}

function getCareerServicePrice(serviceId, packageType) {
  const service = CAREER_SERVICES[serviceId];
  if (!service) return 0;

  if (service.hasPackages && packageType) {
    return service.packages[packageType]?.price || 0;
  }
  return service.oneTimePrice || 0;
}

function getAcademicServicePrice(serviceId, tier, level) {
  const service = ACADEMIC_SERVICES[serviceId];
  if (!service || !service.tiers[tier] || !service.tiers[tier][level]) return 0;
  return service.tiers[tier][level].price;
}

function getAcademicWordRate(serviceId, tier, level) {
  const service = ACADEMIC_SERVICES[serviceId];
  if (!service || !service.tiers[tier] || !service.tiers[tier][level]) return 0;
  return service.tiers[tier][level].wordRate;
}

function calculateComboDiscount(services) {
  if (!Array.isArray(services) || services.length < 2) {
    const price = services?.[0]?.price || 0;
    return { subtotal: price, discount: 0, discountPercent: 0, total: price };
  }

  const subtotal = services.reduce((sum, s) => sum + (s.price || 0), 0);
  const discountPercent = DISCOUNT_CONFIG.EXTERNAL_COMBO_PERCENT;
  const discount = Math.round(subtotal * (discountPercent / 100) * 100) / 100;
  const total = Math.max(DISCOUNT_CONFIG.MIN_PRICE, subtotal - discount);

  return { subtotal, discount, discountPercent, total };
}

function formatPrice(price, currency = "USD") {
  const symbols = { USD: "$", EUR: "€", GBP: "£" };
  return `${symbols[currency] || "$"}${price.toFixed(2)}`;
}

//  ====== API catalog hydration ————————————————————————————————————————————————————
// Maps catalog API service codes ’ local CAREER_SERVICES keys.
// Includes both legacy services.json codes (C01-C05) and live MongoDB serviceIds.
const CAREER_API_CODE_MAP = {
  // Legacy services.json codes
  C01: "resume",
  C02: "cover_letter",
  C03: "linkedin",
  C04: "interview_prep",
  C05: "job_search",
  // MongoDB serviceIds (live from admin panel)
  "resume-writing": "resume",
  "cover-letter": "cover_letter",
  "linkedin-optimization": "linkedin",
  "interview-preparation": "interview_prep",
  "salary-negotiation": "salary_negotiation",
  "personal-branding": "personal_branding",
  "statement-of-purpose": "sop",
  "personal-statement": "sop",
  "letter-of-recommendation": "lor",
  "industry-transition": "industry_transition",
  "executive-cv": "web_resume",
  "reference-letters": "reference_letters",
  "networking-email": "networking_email",
  "personal-website": "personal_website",
};

// Maps catalog API service codes ’ local ACADEMIC_SERVICES keys.
// Includes both legacy services.json codes (A01-A11) and live MongoDB serviceIds.
const ACADEMIC_API_CODE_MAP = {
  // Legacy services.json codes
  A01: "research_paper",
  A02: "dissertation",
  A03: "essay",
  A04: "coursework",
  A05: "literature_review",
  A06: "case_study",
  A07: "lab_report",
  A08: "presentation",
  A09: "annotated_bibliography",
  A10: "reflective_journal",
  A11: "research_proposal",
  // MongoDB serviceIds (live from admin panel)
  "research-paper": "research_paper",
  "research-proposal": "research_proposal",
  "essay-writing": "essay",
  "dissertation-chapter": "dissertation",
  "thesis-writing": "dissertation",
  "literature-review": "literature_review",
  "case-study": "case_study",
  "lab-report": "lab_report",
  "annotated-bibliography": "annotated_bibliography",
  "academic-presentation": "presentation",
  "editing-proofreading": "essay", // closest match for proofreading
};

/* *
 * Fetches /api/pricing/catalog and updates CAREER_SERVICES and ACADEMIC_SERVICES
 * in-place with live names and basePrice values from the server.
 *
 * Silently falls back to static values if the API is unavailable. */
async function refreshFromApi() {
  try {
    const apiBase =
      window.PortEngine?.getApiUrl?.() ||
      (["localhost", "127.0.0.1"].includes(window.location.hostname)
        ? "http://localhost:5001/api"
        : "https://api.ascenddraft.com/api");

    const res = await fetch(`${apiBase}/pricing/catalog`, {
      signal: AbortSignal.timeout(6000),
    });
    if (!res.ok) return;
    const json = await res.json();
    if (!json.success) return;

    const services = json.data.categories
      ? json.data.categories.flatMap((c) => c.services || [])
      : json.data.services || [];

    // Level multipliers matching academic-services.js tier matrix
    // undergraduate : masters : phd 🎯 8 : 10 : 13
    const LEVEL_MULTS = { undergraduate: 1, masters: 10 / 8, phd: 13 / 8 };
    // Tier ratios: proofreading : draft_refining : model_paper = 7 : 10 : 16
    const TIER_RATIOS = {
      proofreading: 7 / 16,
      draft_refining: 10 / 16,
      model_paper: 1,
    };

    for (const svc of services) {
      //  ==== Career services ——————————————————————————————————————————————————
      const careerKey = CAREER_API_CODE_MAP[svc.code];
      if (careerKey && CAREER_SERVICES[careerKey]) {
        const cs = CAREER_SERVICES[careerKey];
        if (svc.name) cs.name = svc.name;
        if (svc.description) cs.description = svc.description;
        if (svc.fullDescription) cs.fullDescription = svc.fullDescription;
        if (svc.features?.length) cs.features = svc.features;
        // Promotional pricing: salePrice overrides the displayed price
        if (svc.salePrice != null && svc.salePrice > 0) {
          cs.salePrice = svc.salePrice;
          cs.originalPrice = svc.originalPrice ?? 0;
          cs.showingPrice = svc.salePrice;
        } else if (svc.originalPrice != null && svc.originalPrice > 0) {
          cs.originalPrice = svc.originalPrice;
          cs.showingPrice = svc.basePrice ?? cs.oneTimePrice;
        }
        if (svc.basePrice != null && svc.basePrice > 0) {
          // Scale package prices proportionally when the oneTimePrice changes
          if (cs.hasPackages && cs.packages && cs.oneTimePrice > 0) {
            const ratio = svc.basePrice / cs.oneTimePrice;
            for (const pkg of Object.values(cs.packages)) {
              if (pkg.price) pkg.price = Math.round(pkg.price * ratio);
              if (pkg.originalPrice)
                pkg.originalPrice = Math.round(pkg.originalPrice * ratio);
            }
          }
          cs.oneTimePrice = svc.basePrice;
          if (!cs.showingPrice) cs.showingPrice = svc.basePrice;
        }
        // Admin-configured sub-service varieties (Service.parentServiceId),
        // e.g. different presentation/CV package options under one parent —
        // API sub-service prices arrive as priceGBP/priceNGN, mapped to the
        // site's priceUSD/priceNGN fields (base display currency, no unit
        // conversion — same convention as oneTimePrice above).
        if (svc.subServices && svc.subServices.length > 0) {
          cs.hasSubServices = true;
          cs.subServices = svc.subServices.map((sub) => ({
            id: sub.id,
            name: sub.name,
            icon: sub.icon || "📄",
            priceUSD: sub.priceGBP ?? 0,
            priceNGN: sub.priceNGN ?? Math.round((sub.priceGBP ?? 0) * 1200),
            description: sub.description || "",
            accompanyingServices: Array.isArray(sub.accompanyingServices)
              ? sub.accompanyingServices
              : [],
          }));
        }
        // Accompanying services (cross-sell add-ons configured in the
        // Service Catalog) — each keeps its own independent price/discount,
        // added as its own basket line item alongside this one (see
        // career-services.js toggleService()).
        if (Array.isArray(svc.accompanyingServices)) {
          cs.accompanyingServices = svc.accompanyingServices;
        }
      }

      //  ==== Academic services ————————————————————————————————————————————————
      const academicKey = ACADEMIC_API_CODE_MAP[svc.code];
      if (academicKey && ACADEMIC_SERVICES[academicKey]) {
        const as = ACADEMIC_SERVICES[academicKey];
        if (svc.name) as.name = svc.name;
        if (svc.description) as.description = svc.description;
        // Only inject word rates when the service is priced per 100 words.
        const isWordRate = svc.unit && svc.unit.includes("100 words");
        if (isWordRate && svc.basePrice != null && svc.basePrice > 0) {
          const ugRate = svc.basePrice / 100; // model_paper undergraduate rate ($/word)
          // Apply the full tier × level matrix so all 9 rates are consistent
          for (const [tier, tr] of Object.entries(TIER_RATIOS)) {
            if (!as.tiers?.[tier]) continue;
            for (const [level, lm] of Object.entries(LEVEL_MULTS)) {
              if (!as.tiers[tier][level]) continue;
              as.tiers[tier][level] = {
                ...as.tiers[tier][level],
                wordRate: parseFloat((ugRate * tr * lm).toFixed(6)),
              };
            }
          }
        }
      }
    }

    // Inject career services from the API that have no local code-map entry
    const mappedCareerCodes = new Set(Object.keys(CAREER_API_CODE_MAP));
    const mappedAcademicCodes = new Set(Object.keys(ACADEMIC_API_CODE_MAP));
    for (const svc of services) {
      if (svc.category !== 'career') continue;
      if (mappedCareerCodes.has(svc.code) || mappedAcademicCodes.has(svc.code)) continue;
      if (CAREER_SERVICES[svc.code]) continue; // already exists
      if (!svc.basePrice && !svc.priceRange) continue;
      CAREER_SERVICES[svc.code] = {
        id: svc.code,
        name: svc.name || svc.code,
        hasWordRate: false,
        oneTimePrice: svc.basePrice ?? 0,
        originalPrice: svc.originalPrice ?? 0,
        salePrice: svc.salePrice ?? 0,
        deliveryDays: svc.deliveryTime ?? 5,
        description: svc.description || '',
        fullDescription: svc.fullDescription || '',
        features: svc.features || [],
        icon: svc.icon || '📄',
        hasSubServices: Array.isArray(svc.subServices) && svc.subServices.length > 0,
        subServices: Array.isArray(svc.subServices)
          ? svc.subServices.map((sub) => ({
              id: sub.id,
              name: sub.name,
              icon: sub.icon || '📄',
              priceUSD: sub.priceGBP ?? 0,
              priceNGN: sub.priceNGN ?? Math.round((sub.priceGBP ?? 0) * 1200),
              description: sub.description || '',
              accompanyingServices: Array.isArray(sub.accompanyingServices)
                ? sub.accompanyingServices
                : [],
            }))
          : [],
        accompanyingServices: Array.isArray(svc.accompanyingServices)
          ? svc.accompanyingServices
          : [],
        _injectedFromApi: true,
      };
    }

    console.log("[PricingData] Refreshed from API catalog.");
  } catch (err) {
    console.warn(
      "[PricingData] refreshFromApi failed — using static data.",
      err.message,
    );
  }
}

// Public API
const PricingData = {
  DISCOUNT_CONFIG,
  CAREER_SERVICES,
  ACADEMIC_SERVICES,
  getCareerService,
  getAcademicService,
  getCareerServicePrice,
  getAcademicServicePrice,
  getAcademicWordRate,
  calculateComboDiscount,
  formatPrice,
  refreshFromApi,
};

// ES Module export
export default PricingData;
export {
  DISCOUNT_CONFIG,
  CAREER_SERVICES,
  ACADEMIC_SERVICES,
  getCareerService,
  getAcademicService,
  getCareerServicePrice,
  getAcademicServicePrice,
  getAcademicWordRate,
  calculateComboDiscount,
  formatPrice,
  refreshFromApi,
};

// Global for backward compatibility
if (typeof window !== "undefined") {
  window.PricingData = PricingData;
}
